import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/layout/PublicHeader";
import { toast } from "sonner";
import { ArrowRight, Users } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

export default function AgencyRegister() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  useSEO({
    title: "Agency Sign-up — Maqam",
    description: "Plug your roster into Maqam's curated Premium list. 15 % commission on every employer hire.",
    path: "/agencies/register",
  });

  const [form, setForm] = useState({
    email: "",
    password: "",
    full_name: "",
    company_name: "",
    company_size: "1-10",
  });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/agency/register", form);
      localStorage.setItem("jf_token", r.data.token);
      setUser(r.data.user);
      toast.success(`Welcome ${r.data.user.full_name}`);
      navigate("/agencies/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0c0d] text-white">
      <PublicHeader />
      <section className="max-w-2xl mx-auto px-6 md:px-10 py-16 lg:py-24">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 mb-6 font-mono text-xs uppercase tracking-widest">
            <Users className="w-3.5 h-3.5" /> For Agencies
          </div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-[0.95] mb-4">
            Sign your agency up.
          </h1>
          <p className="text-white/65">
            Free verification. Within 24 h you'll get an Agency dashboard where
            you can upload workers to Maqam's curated Premium list and earn
            commission on every placement.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5 border border-white/10 p-6 bg-[#141416]" data-testid="agency-register-form">
          <div className="grid md:grid-cols-2 gap-5">
            <label>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">Your name</span>
              <input required value={form.full_name} onChange={change("full_name")} className="w-full bg-transparent border-b-2 border-white/15 focus:border-white outline-none py-3 text-white" data-testid="ar-name" />
            </label>
            <label>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">Agency name</span>
              <input required value={form.company_name} onChange={change("company_name")} className="w-full bg-transparent border-b-2 border-white/15 focus:border-white outline-none py-3 text-white" data-testid="ar-company" />
            </label>
            <label>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">Work email</span>
              <input type="email" required value={form.email} onChange={change("email")} className="w-full bg-transparent border-b-2 border-white/15 focus:border-white outline-none py-3 text-white" data-testid="ar-email" />
            </label>
            <label>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">Password</span>
              <input type="password" required minLength={10} pattern="(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\W_]).{10,}" title="At least 10 characters with uppercase, lowercase, number and special character." value={form.password} onChange={change("password")} className="w-full bg-transparent border-b-2 border-white/15 focus:border-white outline-none py-3 text-white" data-testid="ar-password" />
            </label>
            <label className="md:col-span-2">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">Agency size</span>
              <select value={form.company_size} onChange={change("company_size")} className="w-full bg-transparent border-b-2 border-white/15 focus:border-white outline-none py-3 text-white">
                {["1-10", "11-50", "51-200", "201-1000", "1000+"].map((s) => <option key={s} className="bg-[#141416]">{s}</option>)}
              </select>
            </label>
          </div>
          <button disabled={loading} className="w-full bg-white text-black hover:bg-white/85 font-bold py-5 flex items-center justify-center gap-3 transition-colors disabled:opacity-60" data-testid="ar-submit">
            {loading ? "…" : "Create agency account"} <ArrowRight className="w-5 h-5" />
          </button>
          <div className="text-sm text-center text-white/55">
            Already on Maqam? <Link to="/login" className="link-underline text-white font-medium">Login</Link>
          </div>
        </form>
      </section>
    </div>
  );
}
